<mat-card #sheet appearance="outlined" class="panel-sheet">
  <mat-card-header class="panel-header">
    <mat-card-title id="dashboard-customize-panel-title">{{
      'Dashboard.Panel.Title' | translate: locale.language
    }}</mat-card-title>
    <button
      #closeButton
      mat-icon-button
      type="button"
      class="panel-close"
      [attr.aria-label]="'Dashboard.Panel.Close' | translate: locale.language"
      (click)="session.cancel()">
      <mat-icon>close</mat-icon>
    </button>
  </mat-card-header>
  <mat-divider />

  <mat-card-content class="panel-view">
    <div class="panel-view-row">
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="panel-view-select">
        <mat-label>{{ 'Dashboard.Panel.View' | translate: locale.language }}</mat-label>
        <mat-select
          [value]="session.editedView()?.id ?? defaultViewOption"
          [disabled]="views.busy()"
          (selectionChange)="selectView($event.value)">
          <mat-option [value]="defaultViewOption">{{ views.defaultViewName }}</mat-option>
          @for (view of views.views(); track view.id) {
            <mat-option [value]="view.id">{{
              view.id === session.editedView()?.id ? draftName() : view.name
            }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <button
        #menuTrigger
        mat-icon-button
        type="button"
        [matMenuTriggerFor]="viewMenu"
        [attr.aria-label]="'Dashboard.Panel.MoreActions' | translate: locale.language">
        <mat-icon>more_vert</mat-icon>
      </button>
      <mat-menu #viewMenu="matMenu">
        @if (editingSavedView()) {
          <button mat-menu-item type="button" (click)="rename()">
            <mat-icon>edit</mat-icon>
            <span>{{ 'Dashboard.Panel.RenameView' | translate: locale.language }}</span>
          </button>
        }

        <button mat-menu-item type="button" (click)="session.startFromDefault()">
          <mat-icon>restart_alt</mat-icon>
          <span>{{ 'Dashboard.Panel.StartFromDefault' | translate: locale.language }}</span>
        </button>
        @if (editingSavedView()) {
          <button mat-menu-item type="button" (click)="askToDelete()">
            <mat-icon>delete</mat-icon>
            <span>{{ 'Dashboard.Panel.DeleteView' | translate: locale.language }}</span>
          </button>
        }
      </mat-menu>
    </div>
    @if (views.readOnly()) {
      <p class="mat-caption panel-text panel-error" role="alert">
        {{ 'Dashboard.Views.Unreadable' | translate: locale.language }}
      </p>
      <button
        #resetButton
        mat-stroked-button
        type="button"
        class="panel-reset"
        [disabled]="views.busy() || confirmingReset()"
        (click)="askToReset()">
        {{ 'Dashboard.Panel.ResetViews' | translate: locale.language }}
      </button>
    } @else if (views.atLimit()) {
      <p class="mat-caption panel-text">
        {{ 'Dashboard.Panel.AtLimit' | translate: locale.language : { count: maxViews } }}
      </p>
    }
  </mat-card-content>
  <mat-divider />

  <mat-card-content class="panel-body">
    <section aria-labelledby="dashboard-customize-panel-widgets">
      <h3 class="mat-subtitle-1" id="dashboard-customize-panel-widgets">
        {{ 'Dashboard.Panel.Widgets' | translate: locale.language }}
      </h3>
      <mat-list>
        @for (entry of catalog(); track entry.definition.type) {
          <mat-list-item class="widget-item">
            <mat-icon matListItemIcon aria-hidden="true">{{ entry.definition.icon }}</mat-icon>
            <div matListItemTitle class="widget-row">
              <span class="widget-title">
                {{ entry.definition.title | translate: locale.language }}
                @if (entry.multiple && entry.count > 1) {
                  ({{ entry.count }})
                }
              </span>
              <span class="widget-add-slot">
                @if (entry.multiple) {
                  <button
                    mat-icon-button
                    type="button"
                    [disabled]="entry.atLimit"
                    [attr.aria-label]="
                      'Dashboard.Panel.AddAnother'
                        | translate: locale.language : { title: entry.definition.title | translate: locale.language }
                    "
                    (click)="session.apply({ kind: 'add', type: entry.definition.type })">
                    <mat-icon>add</mat-icon>
                  </button>
                }
              </span>
              <mat-slide-toggle
                [aria-label]="entry.definition.title | translate: locale.language"
                [checked]="entry.count > 0"
                [disabled]="entry.count === 0 && entry.atLimit"
                (change)="
                  $event.checked
                    ? session.apply({ kind: 'add', type: entry.definition.type })
                    : session.apply({ kind: 'removeType', type: entry.definition.type })
                " />
            </div>
          </mat-list-item>
        } @empty {
          <mat-list-item>{{ 'Dashboard.Panel.NoWidgetTypes' | translate: locale.language }}</mat-list-item>
        }
      </mat-list>
    </section>

    <section class="panel-section" aria-labelledby="dashboard-customize-panel-config">
      <h3 class="mat-subtitle-1" id="dashboard-customize-panel-config">
        {{ 'Dashboard.Panel.Configuration' | translate: locale.language }}
      </h3>
      @for (filter of filters(); track filter.field.name) {
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>
            {{
              'Dashboard.Panel.FilterDefault'
                | translate: locale.language : { label: filter.field.filter?.label ?? '' | translate: locale.language }
            }}
          </mat-label>
          <mat-select
            [value]="filter.value"
            (selectionChange)="session.apply({ kind: 'filterDefault', key: filter.field.name, value: $event.value })">
            @for (option of filter.field.filter?.options ?? []; track $index) {
              <mat-option [value]="option.value">{{ option.label | translate: locale.language }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      }

      <div class="widget-row">
        <span class="widget-title">
          {{ 'Dashboard.AutoRefresh' | translate: locale.language : { interval: refreshLabel() } }}
        </span>
        <mat-slide-toggle
          [aria-label]="'Dashboard.AutoRefresh' | translate: locale.language : { interval: refreshLabel() }"
          [checked]="autoRefresh()"
          (change)="session.apply({ kind: 'autoRefresh', enabled: $event.checked })" />
      </div>
    </section>
  </mat-card-content>

  <mat-divider />
  <mat-card-actions align="end" class="panel-actions">
    @if (confirmingDelete()) {
      <p class="mat-body panel-confirm-question" id="dashboard-customize-panel-delete-question">
        {{ 'Dashboard.Panel.DeleteQuestion' | translate: locale.language : { name: draftName() } }}
      </p>
      <button #keepButton mat-button type="button" (click)="keepView()">
        {{ 'Dashboard.Panel.KeepView' | translate: locale.language }}
      </button>
      <button mat-flat-button color="warn" type="button" [disabled]="views.busy()" (click)="session.deleteView()">
        {{ 'Dashboard.Panel.Delete' | translate: locale.language }}
      </button>
    } @else if (confirmingReset()) {
      <p class="mat-body panel-confirm-question">
        {{ 'Dashboard.Panel.ResetQuestion' | translate: locale.language }}
      </p>
      <button #keepViewsButton mat-button type="button" (click)="keepSavedViews()">
        {{ 'Dashboard.Panel.Cancel' | translate: locale.language }}
      </button>
      <button mat-flat-button color="warn" type="button" [disabled]="views.busy()" (click)="resetSavedViews()">
        {{ 'Dashboard.Panel.Reset' | translate: locale.language }}
      </button>
    } @else {
      <button mat-button type="button" (click)="session.cancel()">
        {{ 'Dashboard.Panel.Cancel' | translate: locale.language }}
      </button>
      @if (editingSavedView()) {
        <button mat-stroked-button type="button" [disabled]="cannotSaveNew()" (click)="saveAsNew()">
          {{ 'Dashboard.Panel.SaveAsNew' | translate: locale.language }}
        </button>
        <button
          mat-flat-button
          color="primary"
          type="button"
          [disabled]="views.busy() || views.readOnly()"
          (click)="saveOver()">
          {{ 'Dashboard.Panel.Save' | translate: locale.language }}
        </button>
      } @else {
        <button mat-flat-button color="primary" type="button" [disabled]="cannotSaveNew()" (click)="saveAsNew()">
          {{ 'Dashboard.Panel.SaveView' | translate: locale.language }}
        </button>
      }
    }
  </mat-card-actions>
</mat-card>
